import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import vtkSphereSource from '@kitware/vtk.js/Filters/Sources/SphereSource';
import type {
  vtkSphereSource as VtkSphereSource,
} from '@kitware/vtk.js/Filters/Sources/SphereSource';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import { configStore } from 'config/configStore';

export type InitialPointTuple = [number, number, number];

export type InitialPointMarkerSpec = {
  key: string;
  sourceIndex: InitialPointTuple;
  world: InitialPointTuple;
};

type InitialPointParameter = {
  SavedInfo?: {
    Coronary?: {
      initialPoints1?: unknown;
      initialPoints2?: unknown;
    };
  };
};

type IndexToWorld = (
  index: InitialPointTuple,
  spacing: InitialPointTuple,
  origin: InitialPointTuple,
  half: boolean,
) => InitialPointTuple;

type BuildInitialPointSphereMarkerSpecsParams = {
  parameter?: InitialPointParameter;
  spacing: InitialPointTuple;
  origin: InitialPointTuple;
  dimensions: InitialPointTuple;
  half: boolean;
  indexToWorld: IndexToWorld;
};

type MarkerProperty = {
  set?: (values: Record<string, unknown>) => unknown;
};

export type InitialPointSphereMarkerActor = {
  getMapper?: () => any;
  setMapper?: (mapper: any) => unknown;
  getProperty?: () => MarkerProperty;
  setPickable?: (pickable: boolean) => unknown;
  setForceTranslucent?: (force: boolean) => unknown;
  setForceOpaque?: (force: boolean) => unknown;
  modified?: () => unknown;
  setVisibility?: (visible: boolean) => unknown;
};

type InitialPointSphereMarkerRenderer = {
  addActor?: (actor: any) => unknown;
  removeActor?: (actor: any) => unknown;
};

export type InitialPointSphereMarkerContext = {
  renderer: InitialPointSphereMarkerRenderer;
  overlayRenderer?: InitialPointSphereMarkerRenderer;
  actorIds: Record<string, unknown>;
  viewportId?: string;
};

type CreateInitialPointSphereMarkerActor = (
  prevActor: InitialPointSphereMarkerActor | undefined,
  spec: InitialPointMarkerSpec,
  color: InitialPointTuple,
  radius: number,
  opacity: number,
) => InitialPointSphereMarkerActor;

type SyncInitialPointSphereMarkersOptions = {
  radius: number;
  opacity?: number;
  colors?: Partial<Record<string, InitialPointTuple>>;
  createActor?: CreateInitialPointSphereMarkerActor;
};

export const INITIAL_POINT_SPHERE_MARKER_KEYS = [
  'editLumenInitialPointSphere.1',
  'editLumenInitialPointSphere.2',
] as const;

export const INITIAL_POINT_SPHERE_MARKER_COLORS: Record<
  (typeof INITIAL_POINT_SPHERE_MARKER_KEYS)[number],
  InitialPointTuple
> = {
  [INITIAL_POINT_SPHERE_MARKER_KEYS[0]]: [1, 0, 0],
  [INITIAL_POINT_SPHERE_MARKER_KEYS[1]]: [0, 0, 1],
};

export const INITIAL_POINT_SPHERE_MARKER_OPACITY = 0.5;

const sourceByActor = new WeakMap<
  InitialPointSphereMarkerActor,
  VtkSphereSource
>();

function asInitialPointTuple(value: unknown): InitialPointTuple | null {
  if (!Array.isArray(value) || value.length < 3) return null;
  const point = value.slice(0, 3).map(Number);
  if (point.some((v) => !Number.isFinite(v))) return null;
  return point as InitialPointTuple;
}

function toExisting3DViewportIndex(
  sourceIndex: InitialPointTuple,
  dimensions: InitialPointTuple,
  half: boolean,
): InitialPointTuple {
  const zSize = half ? dimensions[2] * 2 : dimensions[2];
  return [
    sourceIndex[0],
    dimensions[1] - sourceIndex[1] - 1,
    zSize - sourceIndex[2] - 1,
  ];
}

export function buildInitialPointSphereMarkerSpecs({
  parameter,
  spacing,
  origin,
  dimensions,
  half,
  indexToWorld,
}: BuildInitialPointSphereMarkerSpecsParams): InitialPointMarkerSpec[] {
  const coronary = parameter?.SavedInfo?.Coronary;
  const p1 = asInitialPointTuple(coronary?.initialPoints1);
  const p2 = asInitialPointTuple(coronary?.initialPoints2);
  if (!p1 || !p2) return [];

  return [p1, p2].map((sourceIndex, index) => ({
    key: INITIAL_POINT_SPHERE_MARKER_KEYS[index],
    sourceIndex,
    world: indexToWorld(
      toExisting3DViewportIndex(sourceIndex, dimensions, half),
      spacing,
      origin,
      half,
    ),
  }));
}

export function createInitialPointSphereActor(
  prevActor: InitialPointSphereMarkerActor | undefined,
  spec: InitialPointMarkerSpec,
  color: InitialPointTuple,
  radius: number,
  opacity = INITIAL_POINT_SPHERE_MARKER_OPACITY,
): InitialPointSphereMarkerActor {
  const actor = (prevActor ?? vtkActor.newInstance()) as vtkActor;
  let source = sourceByActor.get(actor);

  if (!source) {
    source = vtkSphereSource.newInstance({
      center: spec.world,
      radius,
      thetaResolution: 24,
      phiResolution: 16,
    });
    const mapper = vtkMapper.newInstance();
    mapper.setInputConnection(source.getOutputPort());
    actor.setMapper(mapper);
  } else {
    source.setCenter(...spec.world);
    source.setRadius(radius);
    const mapper = actor.getMapper() as vtkMapper | undefined;
    mapper?.modified?.();
  }
  actor.getProperty().set({
    color,
    ambient: 0.35,
    diffuse: 0.65,
    specular: 0.2,
    opacity,
  });
  actor.setPickable(false);
  actor.setForceOpaque(false);
  actor.setForceTranslucent(true);
  actor.modified();
  sourceByActor.set(actor, source);

  return actor;
}

export function syncInitialPointSphereMarkers(
  context: InitialPointSphereMarkerContext,
  specs: InitialPointMarkerSpec[],
  options: SyncInitialPointSphereMarkersOptions,
): boolean {
  const activeKeys = new Set(specs.map((spec) => spec.key));
  let changed = false;

  INITIAL_POINT_SPHERE_MARKER_KEYS.forEach((key) => {
    if (activeKeys.has(key)) return;
    const actor = context.actorIds[key] as
      | InitialPointSphereMarkerActor
      | undefined;
    if (!actor) return;
    context.overlayRenderer?.removeActor?.(actor);
    context.renderer.removeActor?.(actor);
    delete context.actorIds[key];
    changed = true;
  });

  const targetRenderer = context.overlayRenderer ?? context.renderer;
  const createActor = options.createActor ?? createInitialPointSphereActor;
  const opacity = options.opacity ?? INITIAL_POINT_SPHERE_MARKER_OPACITY;

  specs.forEach((spec) => {
    const prevActor = context.actorIds[spec.key] as
      | InitialPointSphereMarkerActor
      | undefined;
    const color =
      options.colors?.[spec.key] ??
      INITIAL_POINT_SPHERE_MARKER_COLORS[
        spec.key as (typeof INITIAL_POINT_SPHERE_MARKER_KEYS)[number]
      ] ??
      [1, 1, 1];
    const actor = createActor(
      prevActor,
      spec,
      color,
      options.radius,
      opacity,
    );

    actor.setVisibility?.(configStore.get().manual3DTool.initialPoint);
    actor.setPickable?.(true);
    if (!prevActor) {
      targetRenderer.addActor?.(actor);
    }

    context.actorIds[spec.key] = actor;
    changed = true;
  });

  return changed;
}

export function clearInitialPointSphereMarkers(
  context: InitialPointSphereMarkerContext,
): boolean {
  return syncInitialPointSphereMarkers(context, [], {
    radius: 1,
  });
}


export function findInitialPointSphere(
  context: InitialPointSphereMarkerContext,
  point: InitialPointTuple,
): {
  inside: boolean;
  key?: (typeof INITIAL_POINT_SPHERE_MARKER_KEYS)[number];
} {
  for (const key of INITIAL_POINT_SPHERE_MARKER_KEYS) {
    const actor = context.actorIds[key] as
      | InitialPointSphereMarkerActor
      | undefined;

    if (!actor) continue;

    const source = sourceByActor.get(actor);
    if (!source) continue;

    const center = source.getCenter() as InitialPointTuple;
    const radius = source.getRadius();

    const dx = point[0] - center[0];
    const dy = point[1] - center[1];
    const dz = point[2] - center[2];

    if (dx * dx + dy * dy + dz * dz <= radius * radius) {
      return {
        inside: true,
        key,
      };
    }
  }

  return {
    inside: false,
  };
}


export function setInitialPointSphereOpacity(
  context: InitialPointSphereMarkerContext,
  pickId: string | undefined,
  viewportId: string,
): boolean {
  const activeKey = INITIAL_POINT_SPHERE_MARKER_KEYS.includes(
    pickId as (typeof INITIAL_POINT_SPHERE_MARKER_KEYS)[number],
  )
    ? pickId
    : undefined;

  const keys = activeKey
    ? [activeKey]
    : INITIAL_POINT_SPHERE_MARKER_KEYS;

  let changed = false;

  keys.forEach((key) => {
    const actor = context.actorIds[key] as
      | InitialPointSphereMarkerActor
      | undefined;

    const property = actor?.getProperty?.();
    if (!actor || !property) return;

    const targetOpacity = activeKey
      ? 1
      : INITIAL_POINT_SPHERE_MARKER_OPACITY;

    const currentOpacity = (property as any).getOpacity?.();

    if (currentOpacity === targetOpacity) return;

    property.set?.({ opacity: targetOpacity });
    actor.modified?.();

    changed = true;
  });

  if (!changed) return false;

  requestAnimationFrame(() => {
    scheduleRender(viewportId, {
      type: 'updateRenderWindow',
    });
  });

  return true;
}


export function setInitialPointSphereVisibility(
  context: InitialPointSphereMarkerContext,
  visible: boolean,
  viewportId: string,
): boolean {
  let changed = false;

  INITIAL_POINT_SPHERE_MARKER_KEYS.forEach((key) => {
    const actor = context.actorIds[key] as
      | InitialPointSphereMarkerActor
      | undefined;

    if (!actor) return;
    actor.setVisibility?.(visible);
    actor.modified?.();

    changed = true;
  });

  if (!changed) return false;

  scheduleRender(viewportId, {
    type: 'updateRenderWindow',
  });

  return true;
}

export function moveInitialPointSphereIfInside(
  context: InitialPointSphereMarkerContext,
  nextWorldPosition: InitialPointTuple,
  pickId: string,
  viewportId: string,
){
  if (
    !INITIAL_POINT_SPHERE_MARKER_KEYS.includes(
      pickId as (typeof INITIAL_POINT_SPHERE_MARKER_KEYS)[number],
    )
  ) {
    return;
  }

  const actor = context.actorIds[pickId] as
    | InitialPointSphereMarkerActor
    | undefined;
  if (!actor) return;

  const source = sourceByActor.get(actor);
  if (!source) return;
  const property = actor.getProperty?.();
  if (!property) return;

  source.setCenter(...nextWorldPosition);
  source.modified?.();
  actor.getMapper?.()?.modified?.();
  property.set?.({
    opacity: INITIAL_POINT_SPHERE_MARKER_OPACITY,
  });

  actor.modified?.();
  context.actorIds[pickId] = actor;
  scheduleRender(viewportId, {
    type: 'updateRenderWindow',
  });
}

export function getInitialPoints(
  context: InitialPointSphereMarkerContext,
){
  const worlds: [number,number,number][] = [];
  for (const key of INITIAL_POINT_SPHERE_MARKER_KEYS) {
    const actor = context.actorIds[key];
    if (!actor) {
      continue;
    }
    const source = sourceByActor.get(actor);
    if(!source) continue;

    const center = source.getCenter();
    const pos = center;
    if (!pos) {
      continue;
    }
    worlds.push([-pos[0],pos[1],pos[2]]);
  }
  return worlds;
}
